import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { BrandSurface, Identity, SurfaceCategory, SurfaceStatus, SurfaceControlLevel } from "../../../shared/types";

interface SurfaceListResponse {
  data: BrandSurface[];
}

interface SurfaceDetailResponse {
  data: BrandSurface & { capabilities: string[] };
}

interface IdentityListResponse {
  data: Identity[];
}

interface CreateSurfaceInput {
  kind: string;
  category: SurfaceCategory;
  display_name: string;
  control_level: SurfaceControlLevel;
  config?: Record<string, unknown>;
  parent_surface_id?: string | null;
  external_id?: string | null;
  canonical_url?: string | null;
  status?: SurfaceStatus;
  notes?: string | null;
  priority?: number;
}

interface UpdateSurfaceInput {
  display_name?: string;
  control_level?: SurfaceControlLevel;
  config?: Record<string, unknown>;
  parent_surface_id?: string | null;
  external_id?: string | null;
  canonical_url?: string | null;
  status?: SurfaceStatus;
  health_status?: string;
  notes?: string | null;
  priority?: number;
}

interface CreateIdentityInput {
  kind: Identity["kind"];
  display_name: string;
  platform_kind: string;
  external_id?: string | null;
  canonical_url?: string | null;
  config?: Record<string, unknown>;
}

interface UpdateIdentityInput {
  display_name?: string;
  external_id?: string | null;
  canonical_url?: string | null;
  status?: Identity["status"];
  config?: Record<string, unknown>;
}

// ── Surfaces ─────────────────────────────────────────────────────────────────

export function useSurfaces(
  brandId: string | undefined,
  filters?: { category?: string; status?: string; kind?: string }
) {
  const params = new URLSearchParams();
  if (filters?.category) params.set("category", filters.category);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.kind) params.set("kind", filters.kind);
  const queryString = params.toString();

  return useQuery({
    queryKey: ["surfaces", brandId, filters],
    queryFn: () =>
      api.get<SurfaceListResponse>(
        `/api/brands/${brandId}/surfaces${queryString ? `?${queryString}` : ""}`
      ),
    enabled: !!brandId,
  });
}

export function useSurface(brandId: string | undefined, surfaceId: string | undefined) {
  return useQuery({
    queryKey: ["surface", brandId, surfaceId],
    queryFn: () =>
      api.get<SurfaceDetailResponse>(`/api/brands/${brandId}/surfaces/${surfaceId}`),
    enabled: !!brandId && !!surfaceId,
  });
}

export function useCreateSurface(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSurfaceInput) =>
      api.post<SurfaceDetailResponse>(`/api/brands/${brandId}/surfaces`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["surfaces", brandId] });
    },
  });
}

export function useUpdateSurface(brandId: string, surfaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSurfaceInput) =>
      api.put<SurfaceDetailResponse>(`/api/brands/${brandId}/surfaces/${surfaceId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["surface", brandId, surfaceId] });
      qc.invalidateQueries({ queryKey: ["surfaces", brandId] });
    },
  });
}

export function useRetireSurface(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (surfaceId: string) =>
      api.delete<{ data: BrandSurface }>(`/api/brands/${brandId}/surfaces/${surfaceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["surfaces", brandId] });
    },
  });
}

// ── Identities ───────────────────────────────────────────────────────────────

export function useIdentities(brandId: string | undefined) {
  return useQuery({
    queryKey: ["identities", brandId],
    queryFn: () => api.get<IdentityListResponse>(`/api/brands/${brandId}/identities`),
    enabled: !!brandId,
  });
}

export function useCreateIdentity(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIdentityInput) =>
      api.post<{ data: Identity }>(`/api/brands/${brandId}/identities`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identities", brandId] });
    },
  });
}

export function useUpdateIdentity(brandId: string, identityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateIdentityInput) =>
      api.put<{ data: Identity }>(`/api/brands/${brandId}/identities/${identityId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identities", brandId] });
    },
  });
}

export function useRetireIdentity(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (identityId: string) =>
      api.delete<{ data: Identity }>(`/api/brands/${brandId}/identities/${identityId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["identities", brandId] });
    },
  });
}
